import { ReactNode, useState } from "react"
import { Box, Card, HStack, IconButton, Text, VStack } from "@chakra-ui/react"
import { FaChevronRight, FaLock, FaLockOpen } from "react-icons/fa6"
import { ConnectionStatus, getConnectorConfig } from "./constants"
import { ScopeChip } from "../components/ScopeLinkChip"
import { StatusTag } from "../components/Chip"
import { TitleWithChips } from "../components/TitleWithChips"

/**
 * The fields the card renders, and no more. A system's connection comes from a
 * hand-written interface and an asset's straight off the generated GraphQL
 * type, which disagree on which fields are optional. Both satisfy this.
 */
export interface DataConnectionCardFields {
  connectorType: string
  connectorName: string
  externalId?: string | null
  status: string
}

export interface BaseDataConnectionCardProps {
  connection: DataConnectionCardFields
  /** Action buttons rendered on the right side of the header, followed by the lock. Omit for a read-only card: no switch, no lock. */
  actions?: ReactNode
  /** Expandable content rendered below the header (e.g. mapping panel) */
  children?: ReactNode
  /** Whether the card renders its own Card.Root wrapper. Defaults to true. */
  withCardWrapper?: boolean
  /** A row of its own beneath the chips, e.g. which entity the connection is managed on. */
  headerNote?: ReactNode
  /**
   * Whether children sit behind an expand control. Defaults to true. The asset
   * page keeps its one source mapping always visible, having nothing to
   * collapse it away from.
   */
  collapsible?: boolean
  /** Whether collapsible children start expanded. Defaults to true. */
  defaultExpanded?: boolean
  /**
   * A heading in place of the connector name, which moves to the line below.
   * A system names an asset-held connection after its asset.
   */
  title?: ReactNode
  /** A row of chips and links beneath the title, e.g. scope, type, monitoring. */
  chips?: ReactNode
}

export function BaseDataConnectionCard({
  connection,
  actions,
  children,
  withCardWrapper = true,
  headerNote,
  collapsible = true,
  defaultExpanded = true,
  title,
  chips,
}: BaseDataConnectionCardProps) {
  const isActive = connection.status === ConnectionStatus.Active
  const [expanded, setExpanded] = useState(defaultExpanded)
  const hasExpandableContent = children != null && collapsible
  const externalIdLabel = getConnectorConfig(connection.connectorType).externalIdLabel
  // Connectors without an external id label (MQTT, RMS) store an internal id there
  const showExternalId = !!connection.externalId && !!externalIdLabel

  const content = (
    <>
      <HStack justify="space-between" align="start" gap={6}>
        <HStack align="start" gap={2} flex={1} minW={0}>
          {hasExpandableContent && (
            <IconButton
              aria-label={expanded ? "Collapse mapping panel" : "Expand mapping panel"}
              variant="ghost"
              size="xs"
              onClick={() => setExpanded((prev) => !prev)}
              css={{
                transition: "transform 0.2s ease",
                transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <FaChevronRight />
            </IconButton>
          )}
          <VStack align="stretch" gap={1} flex={1} minW={0}>
            {/* The chips beside the name, lined up across the list (AlignedTitles) */}
            <TitleWithChips
              title={title ?? (connection.connectorName || connection.connectorType)}
              chips={
                (chips || showExternalId) && (
                  <>
                    {chips}
                    {showExternalId && (
                      <ScopeChip>
                        {externalIdLabel}: {connection.externalId}
                      </ScopeChip>
                    )}
                  </>
                )
              }
            />
            {headerNote}
            {title != null && (connection.connectorName || connection.connectorType) && (
              <Text fontSize="sm">{connection.connectorName || connection.connectorType}</Text>
            )}
          </VStack>
        </HStack>

        <HStack gap={4} h={6}>
          <StatusTag active={isActive} />
          {/* The lock sits beside the switch: switching on locks the mapping. No switch, no lock. */}
          {actions != null && (
            <HStack gap={2}>
              {actions}
              <Box color={isActive ? "active" : "primary"} fontSize="sm">
                {isActive ? <FaLock /> : <FaLockOpen />}
              </Box>
            </HStack>
          )}
        </HStack>
      </HStack>

      {children != null && (!collapsible || expanded) && (
        <Box mt={4} pt={4} borderTopWidth="1px" borderColor="border.muted">
          {children}
        </Box>
      )}
    </>
  )

  if (!withCardWrapper) return content

  return (
    <Card.Root variant="outline">
      <Card.Body p={4}>
        {content}
      </Card.Body>
    </Card.Root>
  )
}
